import { styled } from "goober";
import { type ReactNode, useCallback, useRef, useState } from "react";
import type { GrantEntry } from "../../shared/rbac/roles";
import { Button } from "./Button";
import { Modal } from "./Modal";
import type { ScopeAccessPendingHandle } from "./ScopeAccessTable";
import { Text } from "./Text";

export const WarningContent = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
`;

export const WarningFooter = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

type PendingWarning = {
  proceed: (grants?: GrantEntry[]) => void;
  count: number;
  canCommit: boolean;
};

export function useScopeAccessCloseWarning({
  singular,
  plural,
}: {
  singular: string;
  plural: string;
}): {
  stagedGrantsRef: { current: ScopeAccessPendingHandle | null };
  requestClose: (proceed: (grants?: GrantEntry[]) => void) => void;
  closeWarningModal: ReactNode;
} {
  const stagedGrantsRef = useRef<ScopeAccessPendingHandle | null>(null);
  const [showWarningModal, setShowWarningModal] =
    useState<PendingWarning | null>(null);

  const requestClose = useCallback(
    (proceed: (grants?: GrantEntry[]) => void) => {
      const pending = stagedGrantsRef.current;
      if (pending && pending.pendingCount > 0) {
        setShowWarningModal({
          proceed,
          count: pending.pendingCount,
          canCommit: pending.canCommit,
        });
        return;
      }
      proceed();
    },
    []
  );

  const proceedAndDismiss = (warning: PendingWarning) => {
    setShowWarningModal(null);
    warning.proceed();
  };

  const closeWarningModal = showWarningModal ? (
    <Modal open onClose={() => setShowWarningModal(null)} alert>
      <WarningContent>
        <Text variant="text-regular-md" color="var(--text-primary)">
          Add before closing?
        </Text>
        <Text variant="text-regular" color="var(--text-secondary)">
          You have {showWarningModal.count}{" "}
          {showWarningModal.count === 1 ? singular : plural}{" "}
          {showWarningModal.canCommit
            ? "selected but not yet added."
            : "selected. Choose a role for each to add them."}
        </Text>
        <WarningFooter>
          <Button
            size="l"
            variant="secondary"
            onClick={() => proceedAndDismiss(showWarningModal)}
          >
            Discard
          </Button>
          {showWarningModal.canCommit ? (
            <Button
              size="l"
              onClick={() => {
                const { proceed } = showWarningModal;
                setShowWarningModal(null);
                // Close once the commit actually lands: immediately for a plain
                // add, or after a deferred grant modal is confirmed.
                stagedGrantsRef.current?.commitPending(proceed);
              }}
            >
              Add and close
            </Button>
          ) : (
            <Button size="l" onClick={() => setShowWarningModal(null)}>
              Keep editing
            </Button>
          )}
        </WarningFooter>
      </WarningContent>
    </Modal>
  ) : null;

  return {
    stagedGrantsRef,
    requestClose,
    closeWarningModal,
  };
}
